/**
 * Component Design Tokens for Watchlists App
 * TypeScript implementation of component-tokens.yaml for easy React integration
 */

import { SxProps } from '@mui/material';
import { getFontFamily } from '../config/fonts';

// ====================
// INTERFACE DEFINITIONS
// ====================

interface ResponsiveValue<T> {
  xs: T;
  sm: T;
  md: T;
}

interface ThemeValue<T> {
  light: T;
  dark: T;
}

interface ButtonState {
  backgroundColor?: string | ThemeValue<string>;
  color?: string | ThemeValue<string>;
  transform?: string;
  boxShadow?: string | ThemeValue<string>;
  borderColor?: string | ThemeValue<string>;
  cursor?: string;
}



// ====================
// MOVIE CARD TOKENS
// ====================





// ====================
// BADGE TOKENS
// ====================

interface BadgeVariantState {
  backgroundColor?: string | ThemeValue<string>;
  color?: string | ThemeValue<string>;
  cursor?: string;
  transform?: string;
  boxShadow?: string | ThemeValue<string>;
}

interface BadgeVariant {
  backgroundColor?: string | ThemeValue<string>;
  color?: string | ThemeValue<string>;
  borderRadius: string;
  padding: ResponsiveValue<string> | Record<string, string>;
  fontSize: ResponsiveValue<string> | Record<string, string>;
  fontWeight?: number;
  minWidth?: string;
  minHeight?: string;
  position?: string;
  top?: string;
  right?: string;
  zIndex?: number;
  border?: string | ThemeValue<string>;
  gap?: string;
  fontFamily?: string;
  textTransform?: string;
  letterSpacing?: string;
  boxShadow?: string | ThemeValue<string>;
  hover?: BadgeVariantState;
  icon?: {
    type?: string;
    size: string | ResponsiveValue<string>;
    color?: string | ThemeValue<string>;
    marginRight?: string;
  };
}

// ====================
// BUTTON TOKENS
// ====================

export const buttonTokens = {
  base: {
    fontFamily: getFontFamily(),
    fontWeight: 600,
    textTransform: 'none' as const,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
  },

  variants: {
    contained: {
      primary: {
        backgroundColor: { light: '#2B2982', dark: '#8B88FF' },
        color: { light: '#FFFFFF', dark: '#FFFFFF' },
        borderRadius: '50px',
        padding: { xs: '8px 16px', sm: '10px 20px', md: '12px 24px' },
        fontSize: { xs: '14px', sm: '15px', md: '16px' },
        hover: {
          backgroundColor: { light: '#1F1F5C', dark: '#A19DFF' },
          transform: 'translateY(-1px)',
          boxShadow: { 
            light: '0 4px 12px rgba(43, 41, 130, 0.3)', 
            dark: '0 4px 12px rgba(139, 136, 255, 0.4)' 
          },
        },
        active: { transform: 'translateY(0px)' },
        disabled: {
          backgroundColor: '#CCCCCC',
          color: '#888888',
          cursor: 'not-allowed',
        },
      },

      secondary: {
        backgroundColor: { light: '#30DB5B', dark: '#30DB5B' },
        color: '#FFFFFF',
        borderRadius: '50px',
        padding: { xs: '8px 16px', sm: '10px 20px', md: '12px 24px' },
        fontSize: { xs: '14px', sm: '15px', md: '16px' },
        hover: {
          backgroundColor: { light: '#28C248', dark: '#28C248' },
          boxShadow: '0 4px 12px rgba(48, 219, 91, 0.3)',
        },
      },

      danger: {
        backgroundColor: '#FF453A',
        color: '#FFFFFF',
        borderRadius: '50px',
        padding: { xs: '8px 16px', sm: '10px 20px', md: '12px 24px' },
        fontSize: { xs: '14px', sm: '15px', md: '16px' },
        hover: {
          backgroundColor: '#E63946',
          boxShadow: '0 4px 12px rgba(255, 69, 58, 0.3)',
        },
      },
    },

    outlined: {
      primary: {
        backgroundColor: 'transparent',
        color: { light: '#2B2982', dark: '#8B88FF' },
        border: { light: '1px solid #2B2982', dark: '1px solid #8B88FF' },
        borderRadius: '50px',
        padding: { xs: '8px 16px', sm: '10px 20px', md: '12px 24px' },
        fontSize: { xs: '14px', sm: '15px', md: '16px' },
        hover: {
          backgroundColor: { 
            light: 'rgba(43, 41, 130, 0.05)', 
            dark: 'rgba(139, 136, 255, 0.1)' 
          },
          borderColor: { light: '#1F1F5C', dark: '#A19DFF' },
        },
      },

      secondary: {
        backgroundColor: 'transparent',
        color: { light: '#000000', dark: '#FFFFFF' },
        border: { 
          light: '1px solid rgba(0,0,0,0.23)', 
          dark: '1px solid rgba(255,255,255,0.23)' 
        },
        borderRadius: '50px',
        padding: { xs: '8px 16px', sm: '10px 20px', md: '12px 24px' },
        fontSize: { xs: '14px', sm: '15px', md: '16px' },
        hover: {
          backgroundColor: { 
            light: 'rgba(0,0,0,0.04)', 
            dark: 'rgba(255,255,255,0.08)' 
          },
        },
      },
    },

    text: {
      primary: {
        backgroundColor: 'transparent',
        color: { light: '#2B2982', dark: '#8B88FF' },
        border: 'none',
        borderRadius: '50px',
        padding: { xs: '6px 16px', sm: '8px 20px', md: '10px 24px' },
        fontSize: { xs: '14px', sm: '15px', md: '16px' },
        hover: {
          backgroundColor: { 
            light: 'rgba(43, 41, 130, 0.1)', 
            dark: 'rgba(139, 136, 255, 0.1)' 
          },
        },
      },

      secondary: {
        backgroundColor: 'transparent',
        color: { light: '#666666', dark: '#CCCCCC' },
        border: 'none',
        borderRadius: '50px',
        padding: { xs: '6px 16px', sm: '8px 20px', md: '10px 24px' },
        fontSize: { xs: '14px', sm: '15px', md: '16px' },
        hover: {
          backgroundColor: { 
            light: 'rgba(0,0,0,0.04)', 
            dark: 'rgba(255,255,255,0.08)' 
          },
        },
      },
    },
  },

  special: {
    fab: {
      primary: {
        backgroundColor: { light: '#2B2982', dark: '#8B88FF' },
        color: '#FFFFFF',
        borderRadius: '50%',
        width: { xs: '56px', sm: '64px', md: '72px' },
        height: { xs: '56px', sm: '64px', md: '72px' },
        boxShadow: {
          light: '0 8px 32px rgba(43, 41, 130, 0.4)',
          dark: '0 8px 32px rgba(139, 136, 255, 0.4)',
        },
        hover: {
          backgroundColor: { light: '#1F1F5C', dark: '#A19DFF' },
          transform: 'scale(1.1)',
          boxShadow: {
            light: '0 12px 40px rgba(43, 41, 130, 0.5)',
            dark: '0 12px 40px rgba(139, 136, 255, 0.5)',
          },
        },
        iconSize: { xs: '28px', sm: '32px', md: '36px' },
      },
    },

    surpriseMe: {
      backgroundColor: '#FF6B35',
      color: '#FFFFFF',
      borderRadius: '50px',
      padding: { xs: '12px 24px', sm: '14px 28px', md: '16px 32px' },
      fontSize: { xs: '14px', sm: '16px', md: '16px' },
      fontWeight: 600,
      boxShadow: '0 8px 32px rgba(255, 107, 53, 0.4)',
      position: 'fixed',
      zIndex: 1000,
      hover: {
        backgroundColor: '#E55A2B',
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 40px rgba(255, 107, 53, 0.5)',
      },
      active: { transform: 'translateY(0px)' },
    },
  },
} as const;

// ====================
// BADGE TOKENS
// ====================

export const badgeTokens = {
  base: {
    fontFamily: getFontFamily(),
    fontWeight: 600,
    textTransform: 'none' as const,
    transition: 'all 0.2s ease',
    cursor: 'default',
    userSelect: 'none' as const,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  variants: {
    status: {
      watched: {
        backgroundColor: { light: '#248A3D', dark: 'rgba(24, 115, 5, 0.9)' },
        borderRadius: '0px 12px 0px 12px',
        padding: { xs: '8px 5px', sm: '8px 5px', md: '8px 5px' },
        fontSize: { xs: '11px', sm: '12px', md: '12px' },
        minWidth: '29px',
        minHeight: '34px',
        position: 'absolute',
        top: '0',
        right: '0',
        zIndex: 2,
        hover: {
          backgroundColor: { light: '#1e6d32', dark: '#28C248' },
          cursor: 'pointer',
        },
        icon: { type: 'checkmark', size: '12px', color: 'rgba(0, 0, 0, 0.9)' },
      } as BadgeVariant,

      watchlist: {
        backgroundColor: { light: '#5856D6', dark: '#5E5CE6' },
        color: '#FFFFFF',
        borderRadius: '0px 12px 0px 12px',
        padding: { xs: '8px 5px', sm: '8px 5px', md: '8px 5px' },
        fontSize: { xs: '11px', sm: '12px', md: '12px' },
        minWidth: '29px',
        minHeight: '34px',
        position: 'absolute',
        top: '0',
        right: '0',
        zIndex: 2,
        hover: {
          backgroundColor: { light: '#4845B8', dark: '#4F4CE6' },
          cursor: 'pointer',
        },
        icon: { type: 'list', size: '12px', color: '#FFFFFF' },
      } as BadgeVariant,

      addToWatchlist: {
        backgroundColor: { light: 'rgba(88, 86, 214, 0.2)', dark: 'rgba(175, 174, 243, 0.2)' },
        color: { light: '#5856D6', dark: '#FFFFFF' },
        borderRadius: '0px 0px 0px 10px',
        padding: { xs: '8px 5px', sm: '8px 5px', md: '8px 5px' },
        fontSize: { xs: '11px', sm: '12px', md: '12px' },
        minWidth: '29px',
        minHeight: '34px',
        position: 'absolute',
        top: '0',
        right: '0',
        zIndex: 2,
        hover: {
          backgroundColor: { light: 'rgba(88, 86, 214, 0.3)', dark: 'rgba(175, 174, 243, 0.3)' },
          cursor: 'pointer',
        },
        icon: { 
          type: 'add', 
          size: '16px', 
          color: { light: '#5856D6', dark: '#FFFFFF' } 
        },
      } as BadgeVariant,
    },

    info: {
      success: {
        backgroundColor: { light: '#34C759', dark: '#30DB5B' },
        color: '#FFFFFF',
        borderRadius: '12px',
        padding: { xs: '4px 8px', sm: '4px 10px', md: '6px 12px' },
        fontSize: { xs: '10px', sm: '11px', md: '12px' },
        fontWeight: 500,
        boxShadow: { 
          light: '0 2px 8px rgba(52, 199, 89, 0.2)', 
          dark: '0 2px 8px rgba(48, 219, 91, 0.3)' 
        },
      } as BadgeVariant,

      warning: {
        backgroundColor: { light: '#FF9500', dark: '#FF9F0A' },
        color: '#FFFFFF',
        borderRadius: '12px',
        padding: { xs: '4px 8px', sm: '4px 10px', md: '6px 12px' },
        fontSize: { xs: '10px', sm: '11px', md: '12px' },
        fontWeight: 500,
        boxShadow: { 
          light: '0 2px 8px rgba(255, 149, 0, 0.2)', 
          dark: '0 2px 8px rgba(255, 159, 10, 0.3)' 
        },
      } as BadgeVariant,

      error: {
        backgroundColor: { light: '#FF453A', dark: '#FF6961' },
        color: '#FFFFFF',
        borderRadius: '12px',
        padding: { xs: '4px 8px', sm: '4px 10px', md: '6px 12px' },
        fontSize: { xs: '10px', sm: '11px', md: '12px' },
        fontWeight: 500,
        boxShadow: { 
          light: '0 2px 8px rgba(255, 69, 58, 0.2)', 
          dark: '0 2px 8px rgba(255, 105, 97, 0.3)' 
        },
      } as BadgeVariant,

      neutral: {
        backgroundColor: { light: '#8E8E93', dark: '#636366' },
        color: '#FFFFFF',
        borderRadius: '12px',
        padding: { xs: '4px 8px', sm: '4px 10px', md: '6px 12px' },
        fontSize: { xs: '10px', sm: '11px', md: '12px' },
        fontWeight: 500,
      } as BadgeVariant,

      private: {
        backgroundColor: { 
          light: 'rgba(142, 142, 147, 0.12)', 
          dark: 'rgba(99, 99, 102, 0.24)' 
        },
        color: { light: '#8E8E93', dark: '#AEAEB2' },
        borderRadius: '8px',
        padding: { xs: '2px 6px', sm: '3px 8px', md: '4px 10px' },
        fontSize: { xs: '9px', sm: '10px', md: '11px' },
        fontWeight: 500,
        border: { 
          light: '1px solid rgba(142, 142, 147, 0.2)', 
          dark: '1px solid rgba(99, 99, 102, 0.3)' 
        },
        icon: { type: 'lock', size: '10px', marginRight: '4px' },
      } as BadgeVariant,
    },

    date: {
      default: {
        backgroundColor: { 
          light: 'rgba(0, 0, 0, 0.08)', 
          dark: 'rgba(255, 255, 255, 0.12)' 
        },
        color: { 
          light: 'rgba(0, 0, 0, 0.7)', 
          dark: 'rgba(255, 255, 255, 0.8)' 
        },
        borderRadius: '8px',
        padding: { xs: '4px 8px', sm: '4px 10px', md: '6px 12px' },
        fontSize: { xs: '10px', sm: '11px', md: '12px' },
        fontWeight: 500,
      } as BadgeVariant,

      timeline: {
        backgroundColor: { light: '#2B2982', dark: '#8B88FF' },
        color: '#FFFFFF',
        borderRadius: '20px',
        padding: { xs: '6px 12px', sm: '8px 16px', md: '10px 20px' },
        fontSize: { xs: '11px', sm: '12px', md: '13px' },
        fontWeight: 600,
        boxShadow: { 
          light: '0 2px 8px rgba(43, 41, 130, 0.2)', 
          dark: '0 2px 8px rgba(139, 136, 255, 0.3)' 
        },
      } as BadgeVariant,
    },

    code: {
      language: {
        backgroundColor: { 
          light: 'rgba(0, 0, 0, 0.06)', 
          dark: 'rgba(255, 255, 255, 0.1)' 
        },
        color: { 
          light: 'rgba(0, 0, 0, 0.8)', 
          dark: 'rgba(255, 255, 255, 0.9)' 
        },
        borderRadius: '4px',
        padding: { xs: '2px 4px', sm: '3px 6px', md: '4px 8px' },
        fontSize: { xs: '9px', sm: '10px', md: '11px' },
        fontWeight: 600,
        fontFamily: "SF Mono, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        border: { 
          light: '1px solid rgba(0, 0, 0, 0.1)', 
          dark: '1px solid rgba(255, 255, 255, 0.15)' 
        },
      } as BadgeVariant,
    },

    rating: {
      star: {
        backgroundColor: { light: '#5856D6', dark: '#8B88FF' },
        color: '#FFFFFF',
        borderRadius: '20px',
        padding: { xs: '6px 12px', sm: '8px 16px', md: '10px 20px' },
        fontSize: { xs: '14px', sm: '16px', md: '18px' },
        fontWeight: 600,
        gap: '4px',
        boxShadow: { 
          light: '0 2px 8px rgba(88, 86, 214, 0.3)', 
          dark: '0 2px 8px rgba(139, 136, 255, 0.4)' 
        },
        icon: { type: 'star', size: '18px', color: '#FFFFFF' },
      } as BadgeVariant,
    },
  },

  sizes: {
    small: { minHeight: '20px', fontSize: '10px', padding: '2px 6px', iconSize: '12px' },
    medium: { minHeight: '24px', fontSize: '12px', padding: '4px 8px', iconSize: '16px' },
    large: { minHeight: '28px', fontSize: '14px', padding: '6px 12px', iconSize: '20px' },
  },

  animations: {
    fadeIn: { transition: 'opacity 0.2s ease', opacity: { from: 0, to: 1 } },
    slideIn: { 
      transition: 'transform 0.3s ease', 
      transform: { from: 'translateY(-10px)', to: 'translateY(0)' } 
    },
    bounce: { 
      transition: 'transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)', 
      transform: { to: 'scale(1.1)' } 
    },
    pulse: {
      animation: 'pulse 2s infinite',
      keyframes: { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } }
    },
  },
} as const;

// ====================
// ICON BUTTON TOKENS
// ====================

export const iconButtonTokens = {
  base: {
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: 'transparent',
  },

  sizes: {
    small: { width: '32px', height: '32px', iconSize: '18px' },
    medium: { width: '40px', height: '40px', iconSize: '20px' },
    large: { width: '48px', height: '48px', iconSize: '24px' },
  },

  variants: {
    default: {
      color: { light: '#000000', dark: '#FFFFFF' },
      hover: {
        backgroundColor: { 
          light: 'rgba(0,0,0,0.04)', 
          dark: 'rgba(255,255,255,0.08)' 
        },
      },
    },

    primary: {
      color: { light: '#2B2982', dark: '#8B88FF' },
      hover: {
        backgroundColor: { 
          light: 'rgba(43, 41, 130, 0.08)', 
          dark: 'rgba(139, 136, 255, 0.12)' 
        },
      },
    },

    close: {
      color: { light: '#666666', dark: '#CCCCCC' },
      hover: {
        backgroundColor: { 
          light: 'rgba(0,0,0,0.04)', 
          dark: 'rgba(255,255,255,0.08)' 
        },
      },
    },
  },
} as const;

// ====================
// NAVIGATION TOKENS
// ====================

export const navigationTokens = {
  bottomTabs: {
    container: {
      backgroundColor: { light: '#FFFFFF', dark: '#1C1C1E' },
      borderTop: { 
        light: '1px solid rgba(0,0,0,0.1)', 
        dark: '1px solid #2C2C2E' 
      },
      position: 'fixed',
      bottom: 0,
      zIndex: 1200,
      elevation: 8,
    },

    tab: {
      minHeight: { xs: '64px', sm: '70px', md: '76px' },
      fontSize: { xs: '10px', sm: '11px', md: '12px' },
      fontWeight: 600,
      fontFamily: getFontFamily(),
      textTransform: 'none' as const,
      padding: { xs: '8px', sm: '12px', md: '16px' },
      gap: { xs: '4px', sm: '6px', md: '8px' },

      default: {
        color: { 
          light: 'rgba(60,60,67,0.6)', 
          dark: 'rgba(255,255,255,0.6)' 
        },
        iconColor: { 
          light: 'rgba(60,60,67,0.6)', 
          dark: 'rgba(255,255,255,0.6)' 
        },
      },

      selected: {
        color: { light: '#2B2982', dark: '#8B88FF' },
        iconColor: { light: '#2B2982', dark: '#8B88FF' },
      },
    },

    iconSizes: { xs: '20px', sm: '22px', md: '24px' },
  },

  customTabs: {
    base: {
      display: 'flex',
      gap: { xs: '8px', sm: '8px', md: '12px' },
    },

    tab: {
      height: '40px',
      borderRadius: '16px',
      padding: '0px 12px',
      fontSize: '14px',
      fontFamily: getFontFamily(),
      lineHeight: 1.5,
      textTransform: 'none' as const,
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 auto',
    },

    states: {
      default: {
        backgroundColor: 'transparent',
        border: { light: '1px solid #C6C6C8', dark: '1px solid #545456' },
        color: { light: '#000000', dark: '#FFFFFF' },
        fontWeight: { light: 500, dark: 400 },
      },
      selected: {
        backgroundColor: { light: '#000000', dark: '#FFFFFF' },
        border: { light: '1px solid rgba(60,60,67,0.18)', dark: '1px solid rgba(255,255,255,0.18)' },
        color: { light: '#FFFFFF', dark: '#000000' },
        fontWeight: { light: 600, dark: 500 },
      },
      hover: {
        backgroundColor: { 
          selected: { light: '#000000', dark: '#FFFFFF' },
          default: { light: 'rgba(0,0,0,0.05)', dark: 'rgba(255,255,255,0.05)' }
        },
      },
    },
  },
} as const;

// ====================
// DESIGN TOKENS
// ====================

export const borderRadiusTokens = {
  none: '0px',
  xs: '2px',
  sm: '4px',
  base: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  pill: '50px',
  circle: '50%',
} as const;

export const shadowTokens = {
  none: 'none',
  sm: '0 2px 8px rgba(0,0,0,0.05)',
  base: '0 4px 12px rgba(0,0,0,0.1)',
  md: '0 8px 20px rgba(0,0,0,0.12)',
  lg: '0 12px 32px rgba(0,0,0,0.15)',
  xl: '0 16px 40px rgba(0,0,0,0.2)',

  primary: { 
    light: '0 4px 12px rgba(43, 41, 130, 0.3)', 
    dark: '0 4px 12px rgba(139, 136, 255, 0.4)' 
  },
  secondary: '0 4px 12px rgba(48, 219, 91, 0.3)',
  danger: '0 4px 12px rgba(255, 69, 58, 0.3)',
  orange: '0 8px 32px rgba(255, 107, 53, 0.4)',

  hover: {
    primary: { 
      light: '0 6px 16px rgba(43, 41, 130, 0.4)', 
      dark: '0 6px 16px rgba(139, 136, 255, 0.5)' 
    },
    secondary: '0 6px 16px rgba(48, 219, 91, 0.4)',
    orange: '0 12px 40px rgba(255, 107, 53, 0.5)',
  },
} as const;

export const spacingTokens = {
  0: '0px',
  0.5: '2px',
  1: '4px',
  1.5: '6px',
  2: '8px',
  2.5: '10px',
  3: '12px',
  3.5: '14px',
  4: '16px',
  5: '20px',
  6: '24px',
  7: '28px',
  8: '32px',
  9: '36px',
  10: '40px',
  12: '48px',
  14: '56px',
  16: '64px',
  20: '80px',
  24: '96px',
  32: '128px',
} as const;

export const colorTokens = {
  brand: {
    primary: { light: '#2B2982', dark: '#8B88FF' },
    primaryHover: { light: '#1F1F5C', dark: '#A19DFF' },
    secondary: { light: '#8B88FF', dark: '#2B2982' },
  },

  semantic: {
    success: '#30DB5B',
    successHover: '#28C248',
    error: '#FF453A',
    warning: '#FF9F0A',
    info: '#0A84FF',
  },

  special: {
    orange: '#FF6B35',
    orangeHover: '#E55A2B',
  },

  text: {
    primary: { light: '#000000', dark: '#FFFFFF' },
    secondary: { light: '#666666', dark: '#CCCCCC' },
    tertiary: { light: '#999999', dark: '#888888' },
    inverse: { light: '#FFFFFF', dark: '#000000' },
  },

  background: {
    primary: { light: '#FFFFFF', dark: '#2C2C2E' },
    secondary: { light: '#F8F8F8', dark: '#1C1C1E' },
    elevated: { light: '#FFFFFF', dark: '#2C2C2E' },
  },

  border: { light: 'rgba(0,0,0,0.1)', dark: 'rgba(255,255,255,0.2)' },
  borderLight: { light: 'rgba(0,0,0,0.05)', dark: 'rgba(255,255,255,0.1)' },
} as const;

export const animationTokens = {
  duration: { fast: '0.15s', normal: '0.3s', slow: '0.5s' },
  easing: {
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
    cubic: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
  transitions: {
    all: 'all 0.3s ease',
    transform: 'transform 0.3s ease',
    opacity: 'opacity 0.3s ease',
    colors: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
  },
} as const;

export const zIndexTokens = {
  base: 1,
  dropdown: 100,
  sticky: 200,
  floating: 1000,
  fab: 1050,
  speedDial: 1050,
  appbar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
  toast: 1600,
  notification: 1700,
  emergency: 2000, // For critical overlays
} as const;

// ====================
// MOVIE CARD TOKENS
// ====================

export const movieCardTokens = {
  base: {
    fontFamily: getFontFamily(),
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    position: 'relative' as const,
    borderRadius: '16px',
    overflow: 'visible' as const,
  },

  sizes: {
    small: {
      poster: { width: '56px', height: '64px' },
      fontSize: '12px',
    },
    medium: {
      poster: { width: '70px', height: '76px' },
      fontSize: '14px',
    },
    large: {
      poster: { width: '84px', height: '96px' },
      fontSize: '16px',
    },
  },

  variants: {
    list: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: { xs: '12px', sm: '16px', md: '16px' },
      padding: { xs: '12px', sm: '16px', md: '16px' },
      backgroundColor: { light: '#FFFFFF', dark: '#2C2C2E' },
      borderRadius: '16px',
      hover: {
        backgroundColor: { light: '#F8F8F8', dark: '#3A3A3C' },
        transform: 'translateX(4px)',
        boxShadow: {
          light: '0 4px 20px rgba(0,0,0,0.1)',
          dark: '0 4px 20px rgba(0,0,0,0.3)',
        },
      },
      poster: {
        aspectRatio: '2/3',
        borderRadius: '8px',
        overflow: 'hidden',
        flexShrink: 0,
      },
    },

    grid: {
      display: 'flex',
      flexDirection: 'column',
      padding: { xs: '12px', sm: '16px', md: '16px' },
      backgroundColor: { light: '#F0EFF0', dark: '#2C2C2E' },
      borderRadius: '16px',
      width: '100%',
      maxWidth: '200px',
      hover: {
        backgroundColor: { light: '#E5E5EA', dark: '#3A3A3C' },
      },
      poster: {
        width: '100%',
        aspectRatio: '2/3',
        borderRadius: '8px',
        overflow: 'hidden',
        marginBottom: { xs: '12px', sm: '12px', md: '12px' },
        backgroundColor: { light: '#F0F0F0', dark: '#3A3A3C' },
      },
    },

    posterOnly: {
      position: 'relative',
      overflow: 'visible',
      hover: {
        transform: 'scale(1.05)',
      },
      poster: {
        borderRadius: '8px',
        overflow: 'hidden',
        flexShrink: 0,
      },
    },

    watchedReview: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: { light: '#FFFFFF', dark: '#2C2C2E' },
      borderRadius: '16px',
      overflow: 'hidden',
      position: 'relative',
      boxShadow: {
        light: '0 2px 12px rgba(0,0,0,0.08)',
        dark: '0 2px 12px rgba(0,0,0,0.4)',
      },
      hover: {
        boxShadow: {
          light: '0 8px 25px rgba(0,0,0,0.15)',
          dark: '0 8px 25px rgba(0,0,0,0.6)',
        },
        transform: 'translateY(-2px)',
      },
      cardContent: {
        padding: { xs: '12px', sm: '16px', md: '16px' },
      },
    },

    portrait: {
      display: 'flex',
      flexDirection: 'column',
      width: { xs: '120px', sm: '140px', md: '160px' },
      position: 'relative',
      hover: {
        transform: 'scale(1.02)',
      },
      poster: {
        width: '100%',
        aspectRatio: '2/3',
        borderRadius: '16px',
        overflow: 'hidden',
        marginBottom: '8px',
        position: 'relative',
        backgroundColor: { light: '#F0F0F0', dark: '#3A3A3C' },
        boxShadow: {
          light: '0 4px 12px rgba(0,0,0,0.1)',
          dark: '0 4px 12px rgba(0,0,0,0.4)',
        },
      },
    },
  },

  content: {
    title: {
      fontWeight: 800,
      color: { light: '#000000', dark: 'theme.palette.text.primary' },
      fontSize: { xs: '14px', sm: '15px', md: '15px' },
      lineHeight: 1.2,
      overflow: 'hidden',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      textOverflow: 'ellipsis',
      marginBottom: '4px',
    },

    year: {
      color: { light: 'rgba(0,0,0,0.6)', dark: 'rgba(255,255,255,0.6)' },
      fontSize: { xs: '11px', sm: '12px', md: '12px' },
      lineHeight: 1.33,
      marginBottom: '4px',
    },

    dateInfo: {
      color: { light: 'rgba(0,0,0,0.5)', dark: 'rgba(255,255,255,0.5)' },
      fontSize: { xs: '10px', sm: '11px', md: '11px' },
      lineHeight: 1.33,
      marginBottom: '8px',
    },

    rating: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginBottom: '8px',
      starIcon: {
        width: '14px',
        height: '14px',
        color: '#FF9500',
      },
      ratingText: {
        fontWeight: 500,
        fontSize: { xs: '11px', sm: '12px', md: '12px' },
        color: { light: 'rgba(0,0,0,0.9)', dark: 'rgba(255,255,255,0.9)' },
        lineHeight: 1.33,
      },
    },

    review: {
      fontSize: { xs: '12px', sm: '14px', md: '14px' },
      color: { light: 'rgba(0,0,0,0.85)', dark: 'rgba(255,255,255,0.85)' },
      lineHeight: 1.43,
      fontWeight: 400,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },

  statusBadge: {
    position: 'absolute' as const,
    top: '0',
    right: '0',
    zIndex: 2,
  },

  responsive: {
    mobileOptimized: {
      padding: '8px',
      fontSize: '12px',
      gap: '8px',
    },
    touchFriendly: {
      minTouchTarget: '44px',
      buttonSpacing: '8px',
    },
  },
} as const;

// ====================
// UTILITY FUNCTIONS
// ====================

/**
 * Get theme-specific value based on current theme mode
 */
export const getThemeValue = <T>(value: T | ThemeValue<T>, isDarkMode: boolean): T => {
  if (typeof value === 'object' && value !== null && 'light' in value && 'dark' in value) {
    return isDarkMode ? (value as ThemeValue<T>).dark : (value as ThemeValue<T>).light;
  }
  return value as T;
};

/**
 * Create responsive sx prop for Material-UI components
 */
export const createResponsiveSx = (value: ResponsiveValue<string>): SxProps => ({
  xs: value.xs,
  sm: value.sm,
  md: value.md,
});

/**
 * Get button styles for Material-UI sx prop
 */
export const getButtonStyles = (
  variant: 'contained' | 'outlined' | 'text',
  color: 'primary' | 'secondary' | 'danger',
  isDarkMode: boolean
): SxProps => {
  const baseStyles = buttonTokens.base;
  const variantStyles = (buttonTokens.variants as any)[variant][color];
  
  return {
    fontFamily: baseStyles.fontFamily,
    fontWeight: baseStyles.fontWeight,
    textTransform: baseStyles.textTransform,
    transition: baseStyles.transition,
    borderRadius: variantStyles.borderRadius,
    backgroundColor: getThemeValue(variantStyles.backgroundColor, isDarkMode),
    color: getThemeValue(variantStyles.color, isDarkMode),
    padding: variantStyles.padding,
    fontSize: variantStyles.fontSize,
    '&:hover': variantStyles.hover ? {
      backgroundColor: variantStyles.hover.backgroundColor ? 
        getThemeValue(variantStyles.hover.backgroundColor, isDarkMode) : undefined,
      transform: variantStyles.hover.transform,
      boxShadow: variantStyles.hover.boxShadow ? 
        getThemeValue(variantStyles.hover.boxShadow, isDarkMode) : undefined,
    } : undefined,
  };
};

/**
 * Get FAB styles for Material-UI sx prop
 */
export const getFabStyles = (isDarkMode: boolean): SxProps => {
  const fabStyles = buttonTokens.special.fab.primary;
  
  return {
    backgroundColor: getThemeValue(fabStyles.backgroundColor, isDarkMode),
    color: fabStyles.color,
    width: fabStyles.width,
    height: fabStyles.height,
    boxShadow: getThemeValue(fabStyles.boxShadow, isDarkMode),
    '&:hover': {
      backgroundColor: getThemeValue(fabStyles.hover.backgroundColor, isDarkMode),
      transform: fabStyles.hover.transform,
      boxShadow: getThemeValue(fabStyles.hover.boxShadow, isDarkMode),
    },
    transition: animationTokens.transitions.all,
  };
};

/**
 * Get icon button styles for Material-UI sx prop
 */
export const getIconButtonStyles = (
  variant: 'default' | 'primary' | 'close',
  size: 'small' | 'medium' | 'large',
  isDarkMode: boolean
): SxProps => {
  const baseStyles = iconButtonTokens.base;
  const sizeStyles = iconButtonTokens.sizes[size];
  const variantStyles = iconButtonTokens.variants[variant];
  
  return {
    ...baseStyles,
    width: sizeStyles.width,
    height: sizeStyles.height,
    color: getThemeValue(variantStyles.color, isDarkMode),
    '&:hover': {
      backgroundColor: getThemeValue(variantStyles.hover.backgroundColor, isDarkMode),
    },
    '& svg': {
      fontSize: sizeStyles.iconSize,
    },
  };
};

// ====================
// BADGE UTILITY FUNCTIONS
// ====================

export const getBadgeStyles = (
  category: 'status' | 'info' | 'date' | 'code' | 'rating',
  variant: string,
  isDarkMode: boolean,
  options?: {
    size?: 'small' | 'medium' | 'large';
    state?: 'watched' | 'unwatched';
    interactive?: boolean;
  }
): SxProps => {
  const baseStyles = badgeTokens.base;
  
  let variantStyles: any;
  
  // Navigate to the correct variant based on category and variant
  switch (category) {
    case 'status':
      variantStyles = badgeTokens.variants.status[variant as 'watched' | 'watchlist' | 'addToWatchlist'];
      break;
    case 'info':
      variantStyles = badgeTokens.variants.info[variant as 'success' | 'warning' | 'error' | 'neutral' | 'private'];
      break;
    case 'date':
      variantStyles = badgeTokens.variants.date[variant as 'default' | 'timeline'];
      break;
    case 'code':
      variantStyles = badgeTokens.variants.code[variant as 'language'];
      break;
    case 'rating':
      variantStyles = badgeTokens.variants.rating[variant as 'star'];
      break;
    default:
      variantStyles = {};
  }

  if (!variantStyles) {
    console.warn(`Badge variant ${category}.${variant} not found`);
    variantStyles = {};
  }

  // Build the styles object
  const styles: SxProps = {
    ...baseStyles,
    borderRadius: variantStyles.borderRadius,
    fontWeight: variantStyles.fontWeight || baseStyles.fontWeight,
    fontFamily: variantStyles.fontFamily || baseStyles.fontFamily,
    textTransform: variantStyles.textTransform || baseStyles.textTransform,
    letterSpacing: variantStyles.letterSpacing,
    gap: variantStyles.gap,
    minWidth: variantStyles.minWidth,
    minHeight: variantStyles.minHeight,
    position: variantStyles.position,
    top: variantStyles.top,
    right: variantStyles.right,
    zIndex: variantStyles.zIndex,
  };

  // Handle background color
  if (variantStyles.backgroundColor) {
    styles.backgroundColor = getThemeValue(variantStyles.backgroundColor, isDarkMode);
  }

  // Handle text color
  if (variantStyles.color) {
    styles.color = getThemeValue(variantStyles.color, isDarkMode);
  }

  // Handle padding based on size or responsive values
  if (variantStyles.padding) {
    if (options?.size && typeof variantStyles.padding === 'object' && variantStyles.padding[options.size]) {
      styles.padding = variantStyles.padding[options.size];
    } else if (typeof variantStyles.padding === 'object' && 'xs' in variantStyles.padding) {
      styles.padding = variantStyles.padding;
    } else {
      styles.padding = variantStyles.padding;
    }
  }

  // Handle font size based on size or responsive values
  if (variantStyles.fontSize) {
    if (options?.size && typeof variantStyles.fontSize === 'object' && variantStyles.fontSize[options.size]) {
      styles.fontSize = variantStyles.fontSize[options.size];
    } else if (typeof variantStyles.fontSize === 'object' && 'xs' in variantStyles.fontSize) {
      styles.fontSize = variantStyles.fontSize;
    } else {
      styles.fontSize = variantStyles.fontSize;
    }
  }

  // Handle border
  if (variantStyles.border) {
    styles.border = getThemeValue(variantStyles.border, isDarkMode);
  }

  // Handle box shadow
  if (variantStyles.boxShadow) {
    styles.boxShadow = getThemeValue(variantStyles.boxShadow, isDarkMode);
  }

  // Handle hover states
  if (variantStyles.hover) {
    const hoverStyles: any = {};
    
    if (variantStyles.hover.backgroundColor) {
      hoverStyles.backgroundColor = getThemeValue(variantStyles.hover.backgroundColor, isDarkMode);
    }

    if (variantStyles.hover.cursor) {
      hoverStyles.cursor = variantStyles.hover.cursor;
    }

    if (variantStyles.hover.transform) {
      hoverStyles.transform = variantStyles.hover.transform;
    }

    if (variantStyles.hover.boxShadow) {
      hoverStyles.boxShadow = getThemeValue(variantStyles.hover.boxShadow, isDarkMode);
    }

    if (Object.keys(hoverStyles).length > 0) {
      (styles as any)['&:hover'] = hoverStyles;
    }
  }

  return styles;
};

export const getStatusBadgeStyles = (
  status: 'watched' | 'watchlist' | 'addToWatchlist',
  isDarkMode: boolean
): SxProps => {
  return getBadgeStyles('status', status, isDarkMode);
};

export const getInfoBadgeStyles = (
  type: 'success' | 'warning' | 'error' | 'neutral' | 'private',
  isDarkMode: boolean,
  size?: 'small' | 'medium' | 'large'
): SxProps => {
  return getBadgeStyles('info', type, isDarkMode, { size });
};

export const getDateBadgeStyles = (
  variant: 'default' | 'timeline',
  isDarkMode: boolean,
  size?: 'small' | 'medium' | 'large'
): SxProps => {
  return getBadgeStyles('date', variant, isDarkMode, { size });
};

export const getCodeBadgeStyles = (
  isDarkMode: boolean,
  size?: 'small' | 'medium' | 'large'
): SxProps => {
  return getBadgeStyles('code', 'language', isDarkMode, { size });
};

export const getRatingBadgeStyles = (
  isDarkMode: boolean,
  size?: 'small' | 'medium' | 'large'
): SxProps => {
  return getBadgeStyles('rating', 'star', isDarkMode, { size });
};

// Icon helper functions for badges
export const getBadgeIconProps = (
  category: 'status' | 'info',
  variant: string,
  options?: {
    size?: 'small' | 'medium' | 'large';
    state?: 'watched' | 'unwatched';
  }
) => {
  let iconConfig: any;

  switch (category) {
    case 'status':
      iconConfig = badgeTokens.variants.status[variant as 'watched' | 'watchlist' | 'addToWatchlist']?.icon;
      break;
    case 'info':
      iconConfig = badgeTokens.variants.info[variant as 'private']?.icon;
      break;
    default:
      iconConfig = null;
  }

  if (!iconConfig) return null;

  return {
    type: iconConfig.type,
    size: options?.size && iconConfig.size[options.size] ? iconConfig.size[options.size] : iconConfig.size,
    color: iconConfig.color,
    marginRight: iconConfig.marginRight,
  };
};

// ====================
// MOVIE CARD UTILITY FUNCTIONS
// ====================

/**
 * Get movie card variant styles for Material-UI sx prop
 */
export const getMovieCardStyles = (
  variant: 'list' | 'grid' | 'poster-only' | 'watched-review' | 'portrait',
  isDarkMode: boolean,
  size?: 'small' | 'medium' | 'large'
): SxProps => {
  const baseStyles = movieCardTokens.base;
  const variantKey = variant === 'poster-only' ? 'posterOnly' : variant === 'watched-review' ? 'watchedReview' : variant;
  const variantStyles = (movieCardTokens.variants as any)[variantKey];
  
  const styles: SxProps = {
    fontFamily: baseStyles.fontFamily,
    transition: baseStyles.transition,
    cursor: baseStyles.cursor,
    position: baseStyles.position,
    borderRadius: baseStyles.borderRadius,
    overflow: baseStyles.overflow,
  };

  // Add variant-specific styles only if they exist
  if (variantStyles?.display) (styles as any).display = variantStyles.display;
  if (variantStyles?.flexDirection) (styles as any).flexDirection = variantStyles.flexDirection;
  if (variantStyles?.alignItems) (styles as any).alignItems = variantStyles.alignItems;
  if (variantStyles?.gap) (styles as any).gap = variantStyles.gap;
  if (variantStyles?.padding) (styles as any).padding = variantStyles.padding;
  if (variantStyles?.width) (styles as any).width = variantStyles.width;
  if (variantStyles?.maxWidth) (styles as any).maxWidth = variantStyles.maxWidth;

  // Handle background color
  if (variantStyles?.backgroundColor) {
    (styles as any).backgroundColor = getThemeValue(variantStyles.backgroundColor, isDarkMode);
  }

  // Handle box shadow
  if (variantStyles?.boxShadow) {
    (styles as any).boxShadow = getThemeValue(variantStyles.boxShadow, isDarkMode);
  }

  // Handle hover states
  if (variantStyles?.hover) {
    (styles as any)['&:hover'] = {
      backgroundColor: variantStyles.hover.backgroundColor ? 
        getThemeValue(variantStyles.hover.backgroundColor, isDarkMode) : undefined,
      transform: variantStyles.hover.transform,
      boxShadow: variantStyles.hover.boxShadow ? 
        getThemeValue(variantStyles.hover.boxShadow, isDarkMode) : undefined,
    };
  }

  return styles;
};

/**
 * Get movie card poster styles for Material-UI sx prop
 */
export const getMovieCardPosterStyles = (
  variant: 'list' | 'grid' | 'poster-only' | 'watched-review' | 'portrait',
  isDarkMode: boolean
): SxProps => {
  const variantKey = variant === 'poster-only' ? 'posterOnly' : variant === 'watched-review' ? 'watchedReview' : variant;
  const variantStyles = (movieCardTokens.variants as any)[variantKey];
  const posterStyles = variantStyles?.poster;

  if (!posterStyles) return {};

  const styles: SxProps = {};

  // Add poster styles only if they exist
  if (posterStyles.width) (styles as any).width = posterStyles.width;
  if (posterStyles.aspectRatio) (styles as any).aspectRatio = posterStyles.aspectRatio;
  if (posterStyles.borderRadius) (styles as any).borderRadius = posterStyles.borderRadius;
  if (posterStyles.overflow) (styles as any).overflow = posterStyles.overflow;
  if (posterStyles.flexShrink !== undefined) (styles as any).flexShrink = posterStyles.flexShrink;
  if (posterStyles.marginBottom) (styles as any).marginBottom = posterStyles.marginBottom;
  if (posterStyles.position) (styles as any).position = posterStyles.position;

  // Handle background color
  if (posterStyles.backgroundColor) {
    (styles as any).backgroundColor = getThemeValue(posterStyles.backgroundColor, isDarkMode);
  }

  // Handle box shadow
  if (posterStyles.boxShadow) {
    (styles as any).boxShadow = getThemeValue(posterStyles.boxShadow, isDarkMode);
  }

  return styles;
};

/**
 * Get movie card content element styles
 */
export const getMovieCardContentStyles = (
  element: 'title' | 'year' | 'dateInfo' | 'rating' | 'review',
  isDarkMode: boolean
): SxProps => {
  const elementStyles = (movieCardTokens.content as any)[element];

  const styles: SxProps = {};

  // Add element styles only if they exist
  if (elementStyles?.fontWeight) (styles as any).fontWeight = elementStyles.fontWeight;
  if (elementStyles?.fontSize) (styles as any).fontSize = elementStyles.fontSize;
  if (elementStyles?.lineHeight) (styles as any).lineHeight = elementStyles.lineHeight;
  if (elementStyles?.overflow) (styles as any).overflow = elementStyles.overflow;
  if (elementStyles?.display) (styles as any).display = elementStyles.display;
  if (elementStyles?.WebkitLineClamp) (styles as any).WebkitLineClamp = elementStyles.WebkitLineClamp;
  if (elementStyles?.WebkitBoxOrient) (styles as any).WebkitBoxOrient = elementStyles.WebkitBoxOrient;
  if (elementStyles?.textOverflow) (styles as any).textOverflow = elementStyles.textOverflow;
  if (elementStyles?.marginBottom) (styles as any).marginBottom = elementStyles.marginBottom;
  if (elementStyles?.alignItems) (styles as any).alignItems = elementStyles.alignItems;
  if (elementStyles?.gap) (styles as any).gap = elementStyles.gap;

  // Handle color
  if (elementStyles?.color) {
    (styles as any).color = getThemeValue(elementStyles.color, isDarkMode);
  }

  return styles;
};

/**
 * Get status badge positioning styles for movie cards
 */
export const getMovieCardStatusBadgeStyles = (): SxProps => {
  return movieCardTokens.statusBadge;
};

// ====================
// CUSTOM TABS UTILITIES
// ====================

export const getCustomTabsContainerStyles = (isDarkMode: boolean): SxProps => ({
  display: navigationTokens.customTabs.base.display,
  gap: navigationTokens.customTabs.base.gap,
});

export const getCustomTabStyles = (
  isSelected: boolean,
  isDarkMode: boolean
): SxProps => {
  const state = isSelected ? navigationTokens.customTabs.states.selected : navigationTokens.customTabs.states.default;
  
  return {
    height: navigationTokens.customTabs.tab.height,
    borderRadius: navigationTokens.customTabs.tab.borderRadius,
    padding: navigationTokens.customTabs.tab.padding,
    fontSize: navigationTokens.customTabs.tab.fontSize,
    fontFamily: navigationTokens.customTabs.tab.fontFamily,
    lineHeight: navigationTokens.customTabs.tab.lineHeight,
    textTransform: navigationTokens.customTabs.tab.textTransform,
    transition: navigationTokens.customTabs.tab.transition,
    cursor: navigationTokens.customTabs.tab.cursor,
    display: navigationTokens.customTabs.tab.display,
    alignItems: navigationTokens.customTabs.tab.alignItems,
    justifyContent: navigationTokens.customTabs.tab.justifyContent,
    flex: navigationTokens.customTabs.tab.flex,
    backgroundColor: getThemeValue(state.backgroundColor, isDarkMode),
    border: getThemeValue(state.border, isDarkMode),
    color: getThemeValue(state.color, isDarkMode),
    fontWeight: getThemeValue(state.fontWeight, isDarkMode),
    '&:hover': {
      backgroundColor: isSelected 
        ? getThemeValue(navigationTokens.customTabs.states.hover.backgroundColor.selected, isDarkMode)
        : getThemeValue(navigationTokens.customTabs.states.hover.backgroundColor.default, isDarkMode),
    },
  };
};

// Export all tokens as a single object for convenience
export const componentTokens = {
  buttons: buttonTokens,
  iconButtons: iconButtonTokens,
  navigation: navigationTokens,
  borderRadius: borderRadiusTokens,
  shadows: shadowTokens,
  spacing: spacingTokens,
  colors: colorTokens,
  animations: animationTokens,
  zIndex: zIndexTokens,
  badges: badgeTokens,
  movieCards: movieCardTokens,
} as const; 